import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
// eslint-disable-next-line
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { HelpTooltipIcon } from 'components/elements/Help/HelpTooltipIcon';
import NoData from 'components/elements/Reports/NoData';
import TimeSeriesBarGraph from 'components/structure/Analytics/TimeSeriesBarGraph';
import StackedBarGraph from 'components/structure/Analytics/StackedBarGraph';
import Top6PieChart, {
  PIE_CHART_HEIGHT,
  PieChartData,
  sliceColours
} from 'components/structure/Analytics/Top6PieChart';
import {
  BookingFrequencyData,
  BookingsFrequencyTable
} from 'components/structure/Booking/BookingAnalytics/BookingsFrequencyTable';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import BookingService, {
  BookingProps,
  BookingAttendanceProps,
  BookingAttendanceResponse
} from 'services/hooks/booking-service';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import ResourceService, {
  ResourceProps,
  resourceCategories
} from 'services/hooks/resource-service';
import { Aggregation } from 'services/hooks/space-service';
import { containerStyle } from 'utils/styles';
import { getBeginningOfDay, getEndOfDay, getFormattedDateTimeString } from 'utils/time';

const LS_KEY_BUILDING = 'bl.bookings.reporting.building';
const LS_KEY_RESOURCE = 'bl.bookings.reporting.resource';

interface Distribution {
  [spaceId: string]: {
    x: string[];
    y: number[];
  };
}

type TransformedData = {
  x: (string | number)[];
  series: {
    timeBookedAndOccupied: number[];
    timeNotBookedButOccupied: number[];
    timeBookedButNotOccupied: number[];
  };
};

function getBookingDistributionByHour(
  bookings: BookingProps[],
  startDate: Date,
  endDate: Date
): Distribution {
  const distribution: Distribution = {};
  const hoursInDay = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));

  bookings.forEach((booking) => {
    const start = new Date(booking.start);
    const end = new Date(booking.end);

    const resource = booking.resource;
    if (!distribution[resource]) {
      distribution[resource] = { x: [...hoursInDay], y: Array(24).fill(0) };
    }

    // Use string manipulation to extract hours and minutes from the booking start and end times
    const startHour = parseInt(booking.start.substring(11, 13));
    const endHour = parseInt(booking.end.substring(11, 13));
    const startMinute = parseInt(booking.start.substring(14, 16));
    const endMinute = parseInt(booking.end.substring(14, 16));

    // Calculate the fraction of the hour for the start and end times
    if (startHour === endHour) {
      // Booking within the same hour
      distribution[resource].y[startHour] += (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    } else {
      // Booking spans multiple hours
      // Add fraction for the first hour
      distribution[resource].y[startHour] += (60 - startMinute) / 60;

      // Add 1 for hours fully covered by the booking
      for (let hour = startHour + 1; hour < endHour; hour++) {
        distribution[resource].y[hour] += 1;
      }

      // Add fraction for the last hour
      if (endMinute > 0) {
        // Check if there's a fraction to add for the last hour
        distribution[resource].y[endHour] += endMinute / 60;
      }
    }
  });

  // Calculate the total days in the range to find the average
  const totalDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1;

  Object.keys(distribution).forEach((resource) => {
    distribution[resource].y = distribution[resource].y.map((hours) => hours / totalDays);
  });

  return distribution;
}

function getBookingDistributionByDayOfWeek(
  bookings: BookingProps[],
  startDate: Date,
  endDate: Date
): Distribution {
  const distribution: Distribution = {};
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  bookings.forEach((booking) => {
    const start = new Date(booking.start);
    const end = new Date(booking.end);

    const resource = booking.resource;
    if (!distribution[resource]) {
      distribution[resource] = { x: [...daysOfWeek], y: Array(7).fill(0) };
    }

    // Calculate the number of hours the booking lasts
    const bookingDurationHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);

    // Determine the day of week for the start of the booking
    const startDayOfWeek = start.getDay(); // getDay() returns 0 for Sunday through 6 for Saturday
    const startIndex = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1; // Adjust index for our daysOfWeek array (Monday to Sunday)

    // Assuming bookings do not span more than one day for simplicity
    distribution[resource].y[startIndex] += bookingDurationHours;
  });

  // Calculate the total weeks in the range to find the average
  const totalDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1;
  const totalWeeks = Math.ceil(totalDays / 7);

  Object.keys(distribution).forEach((resource) => {
    distribution[resource].y = distribution[resource].y.map((hours) => hours / totalWeeks);
  });

  return distribution;
}

function getBookingDistributionByMonthOfYear(
  bookings: BookingProps[],
  startDate: Date,
  endDate: Date
): Distribution {
  const distribution: Distribution = {};
  const monthsOfYear = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  bookings.forEach((booking) => {
    const start = new Date(booking.start);
    const end = new Date(booking.end);

    const resource = booking.resource;
    if (!distribution[resource]) {
      distribution[resource] = { x: [...monthsOfYear], y: Array(12).fill(0) };
    }

    // Calculate the number of hours the booking lasts
    const bookingDurationHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);

    // Determine the month for the start of the booking
    const startMonth = start.getMonth(); // getMonth() returns 0 for January through 11 for December

    // Assuming bookings do not span more than one month for simplicity
    distribution[resource].y[startMonth] += bookingDurationHours;
  });

  // Calculate the total years in the range to find the average
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  const totalYears = endYear - startYear + 1;

  Object.keys(distribution).forEach((resource) => {
    distribution[resource].y = distribution[resource].y.map((hours) => hours / totalYears);
  });

  return distribution;
}

function getBookingDistributionByYear(
  bookings: BookingProps[],
  startDate: Date,
  endDate: Date
): Distribution {
  const distribution: Distribution = {};

  // Generate an array of years from startDate to endDate
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) =>
    (startYear + i).toString()
  );

  bookings.forEach((booking) => {
    const start = new Date(booking.start);
    const end = new Date(booking.end);

    const resource = booking.resource;
    if (!distribution[resource]) {
      distribution[resource] = { x: [...years], y: Array(years.length).fill(0) };
    }

    // Calculate the number of hours the booking lasts
    const bookingDurationHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);

    // Determine the booking start year to avoid conflict with the outer scope variable
    const bookingStartYear = start.getFullYear();

    // Find the index of the booking year in the distribution x-axis
    const yearIndex = years.indexOf(bookingStartYear.toString());
    if (yearIndex !== -1) {
      // Add the booking duration to the corresponding year
      distribution[resource].y[yearIndex] += bookingDurationHours;
    }
  });

  // Calculate the total years in the range to find the average
  const totalYears = endYear - startYear + 1;

  Object.keys(distribution).forEach((resource) => {
    distribution[resource].y = distribution[resource].y.map((hours) => hours / totalYears);
  });

  return distribution;
}

function getBookingsDistribution(
  selectedAggregation: string,
  bookingsData: BookingProps[] | undefined,
  startDate: Date,
  buildingTimezone: string | null | undefined,
  endDate: Date
) {
  const beginningOfDay = getBeginningOfDay(startDate, buildingTimezone ?? '');
  const endOfDay = getEndOfDay(endDate, buildingTimezone ?? '');

  switch (selectedAggregation) {
    case 'hourly':
      return getBookingDistributionByHour(bookingsData ?? [], beginningOfDay, endOfDay);
    case 'daily':
      return getBookingDistributionByDayOfWeek(bookingsData ?? [], beginningOfDay, endOfDay);
    case 'monthly':
      return getBookingDistributionByMonthOfYear(bookingsData ?? [], beginningOfDay, endOfDay);
    case 'yearly':
      return getBookingDistributionByYear(bookingsData ?? [], beginningOfDay, endOfDay);
    default:
      return getBookingDistributionByHour(bookingsData ?? [], beginningOfDay, endOfDay);
  }
}
// Transform data to build pie chart
function getMostBookedResources(
  bookings: BookingProps[],
  resources: ResourceProps[]
): PieChartData {
  // Create a map for resource UUID to resource name
  const resourceNameMap = resources.reduce((map, resource) => {
    if (resource.uuid) {
      map[resource.uuid] = resource.name;
    }
    return map;
  }, {} as { [uuid: string]: string });

  const resourceDurations: { [key: string]: { totalHours: number; name: string } } = {};

  bookings.forEach((booking) => {
    const { resource: resourceUuid, start, end } = booking;
    const durationHours = (new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60);

    if (resourceDurations[resourceUuid]) {
      resourceDurations[resourceUuid].totalHours += durationHours;
    } else {
      resourceDurations[resourceUuid] = {
        totalHours: durationHours,
        name: resourceNameMap[resourceUuid] || 'Unknown Resource'
      };
    }
  });

  const sortedResources = Object.entries(resourceDurations)
    .map(([uuid, data]) => ({ id: uuid, value: data.totalHours, label: data.name }))
    .sort((a, b) => b.value - a.value);

  // Take top 6 resources
  const topResources = sortedResources.slice(0, 6);

  // Group the rest into "Other"
  if (sortedResources.length > 6) {
    const otherResourcesTotal = sortedResources
      .slice(6)
      .reduce((sum, current) => sum + current.value, 0);

    topResources.push({
      id: 'other',
      value: otherResourcesTotal,
      label: 'Other'
    });
  }

  return topResources;
}

// Transform data to use in booking frequency table
function getTimesResourceBooked(
  bookings: BookingProps[],
  resources: ResourceProps[]
): BookingFrequencyData[] {
  // Create a map for resource UUID to resource name
  const resourceNameMap = resources.reduce((map, resource) => {
    if (resource.uuid) {
      map[resource.uuid] = resource.name;
    }
    return map;
  }, {} as { [uuid: string]: string });

  // Tally bookings for each resource
  const bookingCounts: { [uuid: string]: number } = {};

  bookings.forEach((booking) => {
    const resourceUuid = booking.resource;
    if (bookingCounts[resourceUuid]) {
      bookingCounts[resourceUuid]++;
    } else {
      bookingCounts[resourceUuid] = 1;
    }
  });

  // Map the counts to the resource names and create the final array
  const result: BookingFrequencyData[] = Object.entries(bookingCounts).map(
    ([uuid, timesBooked]) => ({
      resource: resourceNameMap[uuid] || 'Unknown Resource',
      timesBooked
    })
  );

  return result;
}

function getTimesResourceTypeBooked(
  bookings: BookingProps[],
  resources: ResourceProps[]
): BookingFrequencyData[] {
  // Create a map for resource UUID to resource type
  const resourceTypeMap = resources.reduce((map, resource) => {
    if (resource.uuid) {
      map[resource.uuid] = resource.type;
    }
    return map;
  }, {} as { [uuid: string]: string });

  // Tally bookings for each resource type
  const bookingTypeCounts: { [type: string]: number } = {};

  bookings.forEach((booking) => {
    const resourceUuid = booking.resource;
    const resourceType = resourceTypeMap[resourceUuid];
    if (resourceType) {
      if (bookingTypeCounts[resourceType]) {
        bookingTypeCounts[resourceType]++;
      } else {
        bookingTypeCounts[resourceType] = 1;
      }
    }
  });

  // Map the counts to the resource types and create the final array
  const result: BookingFrequencyData[] = Object.entries(bookingTypeCounts).map(
    ([type, timesBooked]) => ({
      resource: type,
      timesBooked
    })
  );

  return result;
}

// function to transform the data of api response to populate stacked bar graph.
type AggregationType = keyof BookingAttendanceResponse;
function getTransformedStackedBarData(
  attendanceData: BookingAttendanceResponse,
  aggregation: AggregationType
): TransformedData | null {
  if (!attendanceData || !attendanceData[aggregation]) {
    return null;
  }

  const data = attendanceData[aggregation];

  // Extract the time data and convert to hours
  const timeBookedAndOccupied = data.timeBookedAndOccupied.map((time: number) => time / 60);
  const timeNotBookedButOccupied = data.timeNotBookedButOccupied.map((time: number) => time / 60);
  const timeBookedButNotOccupied = data.timeBookedButNotOccupied.map((time: number) => time / 60);

  return {
    x: data.x,
    series: {
      timeBookedAndOccupied,
      timeNotBookedButOccupied,
      timeBookedButNotOccupied
    }
  };
}
// Split the date and get the date for use in booking attendance
export function getDatePartFromDateTimeString(dateTimeString: string): string {
  return dateTimeString.split('T')[0];
}

export default function BookingReporting() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // get values from localstorage
  const lsSelectedBuildingUuid = localStorage.getItem(LS_KEY_BUILDING);
  const lsSelectedResourceUuid = localStorage.getItem(LS_KEY_RESOURCE);

  const currentDate = new Date();
  const dateLastMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 1,
    currentDate.getDate()
  );
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);

  const [startDate, setStartDate] = useState(dateLastMonth);
  const [endDate, setEndDate] = useState(endOfToday);
  const [resourcesInBuilding, setResourcesInBuilding] = useState<ResourceProps[]>([]);
  const [selectedBuildingUuid, setSelectedBuildingUuid] = useState<string>('');
  const [selectedResourceUuid, setSelectedResourceUuid] = useState<string>('');
  const [selectedAggregation, setSelectedAggregation] = useState<Aggregation>('hourly');
  const [selectedResourceUuidForAttendance, setSelectedResourceUuidForAttendance] =
    useState<string>('');
  const [resourcesInBuildingForAttendance, setResourcesInBuildingForAttendance] = useState<
    ResourceProps[]
  >([]);

  // Buildings query
  const { isLoading: isBuildingsLoading, data: buildingsData } = useQuery<BuildingProps[], Error>(
    'buildings',
    BuildingService.findAllBuildings,
    {
      onError: () => {
        toast.error('Problem fetching buildings');
      }
    }
  );

  // Resources query
  const { isLoading: isAllResourcesLoading, data: allResourcesData } = useQuery<
    ResourceProps[],
    Error
  >('allResources', ResourceService.findAllResources, {
    onError: () => {
      toast.error('Problem fetching amenities');
    }
  });

  // Bookings query
  const { isLoading: isBookingsLoading, data: bookingsData } = useQuery<BookingProps[], Error>(
    ['bookings', selectedBuildingUuid, startDate, endDate],
    () => {
      const selectedBuilding = buildingsData?.find(
        (building) => building.uuid === selectedBuildingUuid
      );

      if (!selectedBuilding?.timezone) {
        return [];
      }

      return BookingService.reportBookings(
        selectedBuildingUuid,
        getFormattedDateTimeString(startDate, selectedBuilding?.timezone),
        getFormattedDateTimeString(endDate, selectedBuilding?.timezone)
      );
    },
    {
      enabled:
        !!selectedBuildingUuid &&
        !!startDate &&
        !!endDate &&
        !!selectedAggregation &&
        !isBuildingsLoading,
      onError: () => {
        toast.error('Problem fetching bookings');
      }
    }
  );

  const { isLoading: isBookingAttendanceLoading, data: attendanceData } = useQuery<
    BookingAttendanceResponse,
    Error
  >(
    [
      'bookingAttendance',
      selectedBuildingUuid,
      selectedResourceUuidForAttendance,
      startDate,
      endDate,
      selectedAggregation,
      buildingsData
    ],
    () => {
      if (
        selectedBuildingUuid &&
        selectedResourceUuidForAttendance &&
        startDate &&
        endDate &&
        selectedAggregation &&
        buildingsData
      ) {
        const selectedBuilding = buildingsData.find(
          (building) => building.uuid === selectedBuildingUuid
        );

        const timezone = selectedBuilding?.timezone || 'UTC';

        if (timezone) {
          // Create a new object of type BookingAttendanceProps
          const newBookingAttendanceProps: BookingAttendanceProps = {
            buildingUuid: selectedBuildingUuid,
            param: selectedResourceUuidForAttendance,
            startDate: getDatePartFromDateTimeString(
              getFormattedDateTimeString(startDate, timezone)
            ),
            endDate: getDatePartFromDateTimeString(getFormattedDateTimeString(endDate, timezone))
          };

          // Return the data using the newly created object
          return BookingService.getBookingAttendanceData(newBookingAttendanceProps);
        }
      }

      return Promise.reject(new Error('Missing required parameters'));
    },
    {
      enabled:
        selectedBuildingUuid !== '' &&
        selectedResourceUuidForAttendance !== '' &&
        !isBuildingsLoading,
      onError: (error) => {
        toast.error('Problem fetching booking attendance: ' + error.message);
      }
    }
  );

  useEffect(() => {
    if (!!selectedBuildingUuid && !!resourcesInBuilding.length) {
      // when the selected building changes, select its first resource
      setSelectedResourceUuid(resourcesInBuilding[0].uuid ?? '');
      setSelectedResourceUuidForAttendance(resourcesInBuilding[0].uuid ?? '');
    }
  }, [selectedBuildingUuid, resourcesInBuilding]);

  useEffect(() => {
    if (!!selectedBuildingUuid && !!allResourcesData) {
      // filter the resources that only apply to the building and store them in the state
      setResourcesInBuilding(
        allResourcesData.filter((resource) => resource.buildingUuid === selectedBuildingUuid)
      );
    }
  }, [selectedBuildingUuid, allResourcesData]);

  useEffect(() => {
    if (!!selectedBuildingUuid && !!allResourcesData && !!resourcesInBuilding) {
      // Set the list of resources for the booking attendance graph
      setResourcesInBuildingForAttendance([
        ...resourcesInBuilding,
        ...resourceCategories.map((resource) => ({
          ...resource,
          buildingUuid: selectedBuildingUuid // Assign the selectedBuildingUuid
        }))
      ]);
    }
  }, [selectedBuildingUuid, allResourcesData, resourcesInBuilding]);

  /**
   * useEffects to restore the last selections from localstorage.
   */

  useEffect(() => {
    // If no building is selected (e.g. initial load), use the last selection stored in localstorage (if it exists)
    if (
      !!buildingsData &&
      !selectedBuildingUuid &&
      lsSelectedBuildingUuid &&
      buildingsData.find((building) => building.uuid === lsSelectedBuildingUuid)
    ) {
      setSelectedBuildingUuid(lsSelectedBuildingUuid);
    }
  }, [buildingsData, lsSelectedBuildingUuid, selectedBuildingUuid]);

  useEffect(() => {
    // If no resource is selected (e.g. initial load), use the last selection stored in localstorage (if it exists)
    if (
      !!resourcesInBuilding &&
      !!resourcesInBuilding.length &&
      !selectedResourceUuid &&
      lsSelectedResourceUuid &&
      resourcesInBuilding.find((resource) => resource.uuid === lsSelectedResourceUuid)
    ) {
      // set the local storage value, if none exists default to the first resource
      setSelectedResourceUuid(lsSelectedResourceUuid || (resourcesInBuilding[0].uuid ?? ''));
    }
  }, [resourcesInBuilding, lsSelectedResourceUuid, selectedResourceUuid]);

  const handleStartDateChange = (date: Date | null) => {
    if (!!date && date < endDate) {
      setStartDate(date);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (!!date && date > startDate) {
      setEndDate(date);
    }
  };

  const handleBuildingChange = (event: SelectChangeEvent) => {
    const building = event.target.value as string;
    setSelectedBuildingUuid(building);
    // store the last selected building in localstorage
    localStorage.setItem(LS_KEY_BUILDING, building);
  };

  const handleResourceChange = (event: SelectChangeEvent) => {
    const resource = event.target.value as string;
    setSelectedResourceUuid(resource);
    // store the last selected resource in localstorage
    localStorage.setItem(LS_KEY_RESOURCE, resource);
  };

  const handleResourceChangeForAttendance = (event: SelectChangeEvent) => {
    const resource = event.target.value as string;
    setSelectedResourceUuidForAttendance(resource);
  };

  const handleAggregationChange = (event: SelectChangeEvent) => {
    const aggregation = event.target.value as Aggregation;
    setSelectedAggregation(aggregation);
  };

  const isLoading = isBookingsLoading || isAllResourcesLoading || isBuildingsLoading;

  const buildingTimezone = buildingsData?.find(
    (building) => building.uuid === selectedBuildingUuid
  )?.timezone;

  const bookings = getBookingsDistribution(
    selectedAggregation,
    bookingsData,
    startDate,
    buildingTimezone,
    endDate
  );

  const selectedResourceUsage = bookings[selectedResourceUuid] ?? { x: [], y: [] };

  const pieChartData = getMostBookedResources(bookingsData ?? [], allResourcesData ?? []);

  const timesResourcesBooked = getTimesResourceBooked(bookingsData ?? [], allResourcesData ?? []);

  const timesResourceTypeBooked = getTimesResourceTypeBooked(
    bookingsData ?? [],
    allResourcesData ?? []
  );

  let transformedStackedBarData: TransformedData | null = null;

  if (attendanceData) {
    transformedStackedBarData = getTransformedStackedBarData(attendanceData, selectedAggregation);
  }

  return (
    <div style={{ marginTop: 40 }}>
      <h1 style={{ paddingBottom: 20 }}>Bookings Reporting</h1>

      <Grid container spacing={1} style={{ paddingBottom: 20, maxWidth: 950 }}>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small" disabled={isBuildingsLoading}>
            <InputLabel>Building</InputLabel>
            <Select
              value={selectedBuildingUuid}
              sx={{ borderRadius: '10px', backgroundColor: 'white' }}
              disabled={!buildingsData}
              onChange={handleBuildingChange}
              label="Building"
            >
              {!!buildingsData &&
                buildingsData
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((building) => {
                    return (
                      <MenuItem key={building.uuid} value={building.uuid}>
                        {building.name}
                      </MenuItem>
                    );
                  })}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <DateTimePicker
            label="Start Date"
            value={startDate}
            onChange={handleStartDateChange}
            slotProps={{
              textField: {
                size: 'small',
                InputProps: {
                  sx: { borderRadius: '10px', backgroundColor: 'white' }
                }
              }
            }}
            maxDateTime={endDate}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <DateTimePicker
            label="End Date"
            value={endDate}
            onChange={handleEndDateChange}
            slotProps={{
              textField: {
                size: 'small',
                InputProps: {
                  sx: { borderRadius: '10px', backgroundColor: 'white' }
                }
              }
            }}
            minDateTime={startDate}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <Select
              value={selectedAggregation}
              onChange={handleAggregationChange}
              sx={{ borderRadius: '10px', backgroundColor: 'white' }}
            >
              <MenuItem value={'hourly'}>Hourly</MenuItem>
              <MenuItem value={'daily'}>Daily</MenuItem>
              <MenuItem value={'monthly'}>Monthly</MenuItem>
              <MenuItem value={'yearly'}>Yearly</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      {/* Timeseries BAR GRAPH*/}
      <div style={containerStyle}>
        <Grid container justifyContent="space-between" sx={{ padding: 2 }}>
          <Grid item xs={6}>
            <Grid container justifyContent="left">
              <Grid item>
                <h4 style={{ marginTop: 10, marginRight: 10 }}>Average Time Used</h4>
              </Grid>
              <Grid item>
                <HelpTooltipIcon
                  message={`The ${selectedAggregation} distribution of amenity bookings, averaged across the selected time span.`}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={6} style={{ maxWidth: 333 }}>
            <FormControl
              fullWidth
              size="small"
              disabled={isAllResourcesLoading || isBuildingsLoading}
            >
              <Select
                value={selectedResourceUuid}
                sx={{ borderRadius: '10px', backgroundColor: 'white' }}
                disabled={!resourcesInBuilding || !resourcesInBuilding.length}
                onChange={handleResourceChange}
              >
                {!!resourcesInBuilding &&
                  resourcesInBuilding
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((resource) => {
                      return (
                        <MenuItem key={resource.uuid} value={resource.uuid}>
                          {resource.name}
                        </MenuItem>
                      );
                    })}
              </Select>
              {!isAllResourcesLoading &&
                !isBuildingsLoading &&
                (!resourcesInBuilding || !resourcesInBuilding.length) && (
                  <FormHelperText>No amenities available for selected building</FormHelperText>
                )}
            </FormControl>
          </Grid>
        </Grid>

        <TimeSeriesBarGraph
          aggregation={selectedAggregation}
          x={selectedResourceUsage.x}
          y={selectedResourceUsage.y}
          loading={isLoading}
          noDataHint="Consider updating your building, amenity, and date filters"
          yAxisLabel="Avg. hours used"
        />
      </div>

      {/* STACKED BAR GRAPH*/}
      <div style={containerStyle}>
        <Grid container justifyContent="space-between" sx={{ padding: 2 }}>
          <Grid item xs={6}>
            <Grid container justifyContent="left">
              <Grid item>
                <h4 style={{ marginTop: 10, marginRight: 10 }}>Bookings Utilization</h4>
              </Grid>
              <Grid item>
                <HelpTooltipIcon message={`The usage of amenities in relation to their bookings`} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={6} style={{ maxWidth: 333 }}>
            <FormControl
              fullWidth
              size="small"
              disabled={isAllResourcesLoading || isBuildingsLoading}
            >
              <Select
                value={selectedResourceUuidForAttendance}
                sx={{ borderRadius: '10px', backgroundColor: 'white' }}
                disabled={
                  !resourcesInBuildingForAttendance || !resourcesInBuildingForAttendance.length
                }
                onChange={handleResourceChangeForAttendance}
              >
                {!!resourcesInBuildingForAttendance &&
                  resourcesInBuildingForAttendance
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((resource) => {
                      return (
                        <MenuItem key={resource.uuid} value={resource.uuid}>
                          {resource.name}
                        </MenuItem>
                      );
                    })}
              </Select>
              {!isAllResourcesLoading &&
                !isBuildingsLoading &&
                (!resourcesInBuilding || !resourcesInBuilding.length) && (
                  <FormHelperText>No amenities available for selected building</FormHelperText>
                )}
            </FormControl>
          </Grid>
        </Grid>

        {
          <StackedBarGraph
            data={transformedStackedBarData}
            title=""
            xAxisLabel={
              selectedAggregation === 'hourly'
                ? 'Hour of Day'
                : selectedAggregation === 'daily'
                ? 'Day of Week'
                : selectedAggregation === 'monthly'
                ? 'Month'
                : 'Year'
            }
            yAxisLabel="Time (hours)"
            isLoading={isBookingAttendanceLoading}
          />
        }
      </div>
      <Grid container wrap={isDesktop ? 'nowrap' : 'wrap'} gap={2} style={{ width: '100%' }}>
        <Grid item xs={12} md={5}>
          <div style={containerStyle}>
            <Grid container justifyContent="left">
              <Grid item>
                <h4 style={{ margin: 10 }}>Types of bookings</h4>
              </Grid>
              <Grid item>
                <HelpTooltipIcon message="The amount of times specific types of amenities were booked across the selected time span" />
              </Grid>
            </Grid>
            <BookingsFrequencyTable resourceRows={timesResourceTypeBooked} loading={isLoading} />
          </div>
          <div style={containerStyle}>
            <Grid container justifyContent="left">
              <Grid item>
                <h4 style={{ margin: 10 }}>Booking Frequency</h4>
              </Grid>
              <Grid item>
                <HelpTooltipIcon message="The amount of times amenities were booked across the selected time span." />
              </Grid>
            </Grid>
            <BookingsFrequencyTable resourceRows={timesResourcesBooked} loading={isLoading} />
          </div>
        </Grid>
        <Grid item xs={12} md={7} style={containerStyle}>
          <Grid container justifyContent="left">
            <Grid item>
              <h4 style={{ margin: 10 }}>Most booked amenities</h4>
            </Grid>
            <Grid item>
              <HelpTooltipIcon message="The most booked amenities across the selected time span." />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12} md={9}>
              <div style={{ width: '99%', marginLeft: 10 }}>
                {isLoading || !!pieChartData.length ? (
                  <Top6PieChart data={pieChartData} loading={isLoading} />
                ) : (
                  <NoData
                    height={PIE_CHART_HEIGHT}
                    hint="No amenities were used within the selected time span."
                  />
                )}
              </div>
            </Grid>
            <Grid item xs={12} md={3}>
              {isLoading ? (
                <Skeleton variant="rectangular" height={600} style={{ marginLeft: 20 }} />
              ) : (
                !!pieChartData &&
                !!pieChartData.length && (
                  <>
                    {pieChartData.map((slice, index) => {
                      return (
                        <div key={slice.id} style={{ paddingBottom: 15 }}>
                          <div
                            style={{
                              color: sliceColours[index],
                              fontSize: '22px',
                              fontWeight: 'bold'
                            }}
                          >
                            {slice.value.toLocaleString()} hours
                          </div>
                          <div style={{ marginTop: -5, fontSize: '14px' }}>{slice.label}</div>
                        </div>
                      );
                    })}
                  </>
                )
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
}
